import React from 'react'

import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'

import { Alert, Button, Modal } from '@repo/ui'
import { resolveActivationTrigger, type ActivationTriggerValue } from '@repo/types'

import { ACTIVATION_TRIGGERS } from 'constants/activationTriggers'
import { AddressPreview } from 'components/display/AddressPreview'
import { SelectionDot } from 'components/display/SelectionDot'
import { useAddresses } from 'hooks/api'
import type { Address } from 'types'

interface SendToLocationModalProps {
  open: boolean
  onClose: () => void
  certificateId: string
  /** Resolved from certificate -> product -> company by the caller. */
  resolvedTrigger: ActivationTriggerValue | null | undefined
  loading?: boolean
  onSend: (addressId: string) => void
}

/**
 * VERA-931 — send one certificate to one of the brand's own locations.
 *
 * WHAT THIS MODAL HAS TO SAY, and why a plain picker was not enough: dispatch
 * itself bills nothing, but it is the first half of a two-step flow whose SECOND
 * half charges the company when the trigger is AT_STORE. Telling someone only
 * "pick a location" hides the consequence until it has already happened at the far
 * end, to a different person, in a different building. So the modal states what
 * receipt will do, and states it DIFFERENTLY depending on the resolved trigger —
 * because for a brand on On sale, arrival genuinely costs nothing, and warning
 * them anyway is the noise that teaches people to ignore the warning that matters.
 *
 * EVERY address is offered, not only shops. The book already holds production
 * sites and warehouses, and those are exactly where a manufacturing brand's
 * activation moment lives — see the AT_LOCATION rename (migration 0101). Each row
 * carries its type icon so "which of these is the factory" is answerable at a
 * glance rather than from the street address.
 */
export const SendToLocationModal = ({
  open,
  onClose,
  certificateId: _certificateId,
  resolvedTrigger,
  loading,
  onSend,
}: SendToLocationModalProps) => {
  const theme = useTheme()
  const { addresses, isLoading } = useAddresses()
  const [selected, setSelected] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (open) setSelected(null)
  }, [open])

  // Every address the brand has. The API accepts any of them (tenancy is the real
  // guard), so filtering here would only hide options that would have worked.
  const locations = addresses as Address[]

  const activatesOnArrival =
    resolveActivationTrigger({ certificate: resolvedTrigger }).trigger === 'AT_LOCATION'

  const triggerTitle =
    ACTIVATION_TRIGGERS.find(
      option => option.id === resolveActivationTrigger({ certificate: resolvedTrigger }).trigger,
    )?.title ?? 'On sale'

  return (
    <Modal
      title="Send to location"
      open={open}
      onClose={loading ? () => {} : onClose}
      actions={
        <Stack direction="row" spacing={3}>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={() => selected && onSend(selected)}
            loading={loading}
            // §54 — dead until a store is chosen. Nothing to send otherwise.
            disabled={loading || !selected}
          >
            Send
          </Button>
        </Stack>
      }
    >
      <Stack spacing={4}>
        {/*
          The copy goes through `description`, NOT children. Children render bare
          inside `.MuiAlert-message`, which the shared Alert colours with
          `tokens.titleColor` — amber (#fbbf24) for a warning — so body prose passed
          as children came out tinted as though every word were the headline. Only
          `description` gets the muted `descriptionColor` that body copy wants. The
          severity-tinted title colour is deliberate in the shared component; the
          wrong prop was the bug. (`ConfirmSettingModal` already does this correctly.)
        */}
        <Alert
          severity={activatesOnArrival ? 'warning' : 'info'}
          description={
            activatesOnArrival
              ? 'This certificate activates when the store confirms it arrived, and activation is what makes it billable. Sending it now does not charge anything. Confirming receipt does.'
              : `Sending this to a store records it in the chain of custody. It does not activate or charge anything: this certificate activates ${triggerTitle.toLowerCase()}.`
          }
        />

        {isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Loading locations…
          </Typography>
        ) : locations.length === 0 ? (
          /*
            An empty state that names the FIX, not just the absence. A brand with no
            store addresses cannot use this feature at all, and "no stores found"
            leaves them to guess where stores come from.
          */
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            No addresses yet. Add one under Settings → Address book — a store, a
            production site, anywhere you receive stock — then send items to it
            from here.
          </Typography>
        ) : (
          <Stack spacing={3}>
            {locations.map(store => {
              const isSelected = store.id === selected
              return (
                <Box
                  key={store.id}
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setSelected(store.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    p: 5,
                    borderRadius: 3,
                    cursor: 'pointer',
                    // §34 selectable card — tint is "considering", border is "chosen".
                    border: `1px solid ${
                      isSelected ? theme.palette.brand[500] : theme.palette.primary[100]
                    }`,
                    backgroundColor: isSelected
                      ? alpha(theme.palette.brand[500], 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isSelected
                        ? alpha(theme.palette.brand[500], 0.1)
                        : alpha(theme.palette.brand[500], 0.06),
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', flexShrink: 0 }}>
                    <SelectionDot selected={isSelected} />
                  </Box>
                  {/* The SHARED preview (§28/§36), not a hand-rolled copy of it — a
                      40px bordered circle around the type icon, then label + the
                      formatted address line. This row used to re-implement it with a
                      bare 18px icon, no container, and its own Typography stack, which
                      is exactly the drift `AddressPreview` exists to end. Now identical
                      to the billing/invoice rows, the address table cell and
                      `DetailField` — the surfaces already on the shared component.

                      Settings → Address book is now one of them too. It used to draw a
                      44px ROUNDED SQUARE (`borderRadius: 2`, `grey[300]`) around a
                      default-size 24px icon, and the fix was to bring it onto this
                      component rather than resize this component to match the outlier —
                      40px circle + 20px icon is the house entity-preview standard, the
                      same 40 `PaymentMethodPreview` uses. */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <AddressPreview address={store} />
                  </Box>
                </Box>
              )
            })}
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
